"use client";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Settings,
  X,
  Save,
  Calendar,
  MapPin,
  User,
  Wrench,
  Factory,
  Hash,
  Fuel,
  FileText,
  Eye,
} from "lucide-react";
import axiosInstance from "../../utils/axiosInstance.js";
import { BASE_URL } from "../../config.js";
import "./machine.css";

function Machine() {
  const navigate = useNavigate();

  const token = JSON.parse(localStorage.getItem("NagpurProperties"))?.token;

  const [showAddMachineForm, setShowAddMachineForm] = useState(false);
  const [machines, setMachines] = useState([]);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  // Machine Form states
  const [machineName, setMachineName] = useState("");
  const [mechinetype, setMechinetype] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [model, setModel] = useState("");
  const [manufactureringYear, setManufactureringYear] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [currentLocation, setCurrentLocation] = useState("");
  const [operatorName, setOperatorName] = useState("");
  const [fuleCapacity, setFuleCapacity] = useState("");
  const [description, setDescription] = useState("");

  // Fuel usage fields
  const [usageType, setUsageType] = useState("PER_KM");
  const [fuelEfficiency, setFuelEfficiency] = useState("");
  const [currentFuel, setCurrentFuel] = useState("0");

  // Fetch all machines
  const fetchMachines = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`${BASE_URL}/machines`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMachines(res.data || []);
      console.log("Fetched machines:", res.data);
    } catch (error) {
      console.error("Error fetching machines:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMachines();
  }, []);

  // Reset machine form
  const resetMachineForm = () => {
    setMachineName("");
    setMechinetype("");
    setManufacturer("");
    setModel("");
    setManufactureringYear("");
    setSerialNumber("");
    setPurchaseDate("");
    setCurrentLocation("");
    setOperatorName("");
    setFuleCapacity("");
    setDescription("");
    setUsageType("PER_KM");
    setFuelEfficiency("");
    setCurrentFuel("0");
    setEditId(null);
  };

  // Submit machine form (add or update)
  const handleMachineSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const efficiencyValue = Number.parseFloat(fuelEfficiency) || 0;

    const machineData = {
      machineName,
      mechinetype,
      manufacturer,
      model,
      manufactureringYear,
      serialNumber,
      purchaseDate,
      currentLocation,
      operatorName,
      fuleCapacity,
      description,
      usageType,
      currentFuel: Number.parseFloat(currentFuel) || 0,

      // Send both fields - the inactive one gets 0
      kmPerLiter: usageType === "PER_KM" ? efficiencyValue : 0,
      literPerHour: usageType === "PER_HOUR" ? efficiencyValue : 0,
    };

    try {
      if (editId) {
        await axiosInstance.put(`${BASE_URL}/machines/${editId}`, machineData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert("Machine updated successfully!");
      } else {
        await axiosInstance.post(`${BASE_URL}/machines`, machineData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert("Machine added successfully!");
      }

      setShowAddMachineForm(false);
      resetMachineForm();
      fetchMachines();
    } catch (error) {
      console.error("Error saving machine:", error);
      alert("Error saving machine. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Edit machine - load data
  const handleEditClick = (machine) => {
    setEditId(machine.id);
    setMachineName(machine.machineName || "");
    setMechinetype(machine.mechinetype || "");
    setManufacturer(machine.manufacturer || "");
    setModel(machine.model || "");
    setManufactureringYear(machine.manufactureringYear || "");
    setSerialNumber(machine.serialNumber || "");
    setPurchaseDate(machine.purchaseDate?.split("T")[0] || "");
    setCurrentLocation(machine.currentLocation || "");
    setOperatorName(machine.operatorName || "");
    setFuleCapacity(machine.fuleCapacity || "");
    setDescription(machine.description || "");

    setUsageType(machine.usageType || "PER_KM");

    // Load the correct efficiency value based on usageType
    if (machine.usageType === "PER_HOUR") {
      setFuelEfficiency(machine.literPerHour?.toString() || "");
    } else {
      // Default to kmPerLiter (also for cases where usageType is missing/wrong)
      setFuelEfficiency(machine.kmPerLiter?.toString() || "");
    }

    setCurrentFuel(machine.currentFuel?.toString() || "0");

    setShowAddMachineForm(true);
  };

  // Delete machine
  const handleDeleteClick = async (id) => {
    if (!window.confirm("Are you sure you want to delete this machine?")) return;

    setLoading(true);
    try {
      await axiosInstance.delete(`${BASE_URL}/machines/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchMachines();
    } catch (error) {
      console.error("Error deleting machine:", error);
      alert("Error deleting machine. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Navigate to view page
  const handleViewClick = (id) => {
    navigate(`/view-machine/${id}`);
  };

  // Filter machines for search
  const filteredMachines = machines.filter(
    (machine) =>
      machine.machineName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      machine.mechinetype?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      machine.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="MachineContainer">
      <div className="MachineMainContent">
        <div className="MachineActionBar">
          <div className="MachineActionBarContent">
            <div className="MachineSearchContainer">
              <Search className="MachineSearchIcon" />
              <input
                type="text"
                placeholder="Search machines by name, type, or manufacturer..."
                className="MachineSearchInput"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="MachineActionButtons">
              <button
                onClick={() => {
                  resetMachineForm();
                  setShowAddMachineForm(true);
                }}
                className="MachineButton MachineButtonPrimary"
              >
                <Plus />
                Add Machine
              </button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="MachineLoading">
            <div className="MachineLoadingContent">
              <div className="MachineSpinner"></div>
              <p className="MachineLoadingText">Loading machines...</p>
            </div>
          </div>
        )}

        <div className="MachineTableContainer">
          <table className="MachineTable">
            <thead className="MachineTableHeader">
              <tr>
                <th className="MachineTableHeaderCell">Machine Details</th>
                <th className="MachineTableHeaderCell">Specifications</th>
                <th className="MachineTableHeaderCell">Purchase Info</th>
                <th className="MachineTableHeaderCell">Location</th>
                <th className="MachineTableHeaderCell">Actions</th>
              </tr>
            </thead>
            <tbody className="MachineTableBody">
              {filteredMachines.map((machine) => (
                <tr key={machine.id} className="MachineTableRow">
                  <td className="MachineTableCell">
                    <div className="MachineCellContent">
                      <div className="MachineCellIcon">
                        <Wrench />
                      </div>
                      <div>
                        <p className="MachineCellTitle">{machine.machineName}</p>
                        <p className="MachineCellSubtitle">{machine.mechinetype}</p>
                      </div>
                    </div>
                  </td>
                  <td className="MachineTableCell">
                    <div>
                      <p className="MachineCellTitle">{machine.manufacturer}</p>
                      <p className="MachineCellSubtitle">Model: {machine.model}</p>
                    </div>
                  </td>
                  <td className="MachineTableCell">
                    <div>
                      <p className="MachineCellSubtitle">{machine.purchaseDate}</p>
                    </div>
                  </td>
                  <td className="MachineTableCell">
                    <div className="MachineLocationContent">
                      <MapPin className="MachineLocationIcon" />
                      <span className="MachineLocationText">{machine.currentLocation}</span>
                    </div>
                  </td>
                  <td className="MachineTableCell">
                    <div className="MachineActionCell">
                      <button
                        onClick={() => handleViewClick(machine.id)}
                        className="MachineActionButton MachineActionButtonView"
                      >
                        <Eye style={{ height: "18px" }} />
                      </button>
                      <button
                        onClick={() => handleEditClick(machine)}
                        className="MachineActionButton MachineActionButtonEdit"
                      >
                        <Edit style={{ height: "18px" }} />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(machine.id)}
                        className="MachineActionButton MachineActionButtonDelete"
                      >
                        <Trash2 style={{ height: "18px" }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="MachineMobileContainer">
          {filteredMachines.map((machine) => (
            <div key={machine.id} className="MachineMobileCard">
              <div className="MachineMobileCardHeader">
                <div className="MachineMobileCardTitle">
                  <div className="MachineCellIcon">
                    <Wrench />
                  </div>
                  <div>
                    <h3 className="MachineCellTitle">{machine.machineName}</h3>
                    <p className="MachineCellSubtitle">{machine.mechinetype}</p>
                  </div>
                </div>
                <div className="MachineMobileCardActions">
                  <button
                    onClick={() => handleViewClick(machine.id)}
                    className="MachineActionButton MachineActionButtonView"
                  >
                    <Eye />
                  </button>
                  <button
                    onClick={() => handleEditClick(machine)}
                    className="MachineActionButton MachineActionButtonEdit"
                  >
                    <Edit />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(machine.id)}
                    className="MachineActionButton MachineActionButtonDelete"
                  >
                    <Trash2 />
                  </button>
                </div>
              </div>
              <div className="MachineMobileCardGrid">
                <div className="MachineMobileCardField">
                  <p className="MachineMobileCardLabel">Manufacturer</p>
                  <p className="MachineMobileCardValue">{machine.manufacturer}</p>
                </div>
                <div className="MachineMobileCardField">
                  <p className="MachineMobileCardLabel">Model</p>
                  <p className="MachineMobileCardValue">{machine.model}</p>
                </div>
                <div className="MachineMobileCardField">
                  <p className="MachineMobileCardLabel">Location</p>
                  <p className="MachineMobileCardValue">{machine.currentLocation}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {!loading && filteredMachines.length === 0 && (
          <div className="MachineEmptyState">
            <div className="MachineEmptyIcon">
              <Settings />
            </div>
            <h3 className="MachineEmptyTitle">No machines found</h3>
            <p className="MachineEmptyDescription">
              {searchTerm
                ? "Try adjusting your search terms"
                : "Get started by adding your first machine"}
            </p>
            {!searchTerm && (
              <button
                onClick={() => {
                  resetMachineForm();
                  setShowAddMachineForm(true);
                }}
                className="MachineButton MachineButtonPrimary"
              >
                <Plus />
                Add Your First Machine
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Machine Form Modal */}
      {showAddMachineForm && (
        <div className="MachineModalOverlay">
          <div className="MachineModal">
            <div className="MachineModalHeader">
              <div className="MachineModalHeaderContent">
                <h2 className="MachineModalTitle">
                  {editId ? "Update Machine" : "Add New Machine"}
                </h2>
                <button
                  onClick={() => {
                    setShowAddMachineForm(false);
                    resetMachineForm();
                  }}
                  className="MachineModalClose"
                >
                  <X />
                </button>
              </div>
            </div>

            <div className="MachineModalContent">
              <form onSubmit={handleMachineSubmit} className="MachineForm">
                <div className="MachineFormGrid">
                  <div className="MachineFormField">
                    <label className="MachineFormLabel">
                      <Wrench />
                      Machine Name
                    </label>
                    <input
                      type="text"
                      className="MachineFormInput"
                      value={machineName}
                      onChange={(e) => setMachineName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="MachineFormField">
                    <label className="MachineFormLabel">
                      <Factory />
                      Machine Type
                    </label>
                    <input
                      type="text"
                      className="MachineFormInput"
                      value={mechinetype}
                      onChange={(e) => setMechinetype(e.target.value)}
                      required
                    />
                  </div>

                  <div className="MachineFormField">
                    <label className="MachineFormLabel">
                      <Factory />
                      Manufacturer
                    </label>
                    <input
                      type="text"
                      className="MachineFormInput"
                      value={manufacturer}
                      onChange={(e) => setManufacturer(e.target.value)}
                      required
                    />
                  </div>

                  <div className="MachineFormField">
                    <label className="MachineFormLabel">
                      <Hash />
                      Model
                    </label>
                    <input
                      type="text"
                      className="MachineFormInput"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                      required
                    />
                  </div>

                  <div className="MachineFormField">
                    <label className="MachineFormLabel">
                      <Calendar />
                      Manufacturing Year
                    </label>
                    <input
                      type="number"
                      className="MachineFormInput"
                      value={manufactureringYear}
                      onChange={(e) => setManufactureringYear(e.target.value)}
                      required
                    />
                  </div>

                  <div className="MachineFormField">
                    <label className="MachineFormLabel">
                      <Hash />
                      Serial Number
                    </label>
                    <input
                      type="text"
                      className="MachineFormInput"
                      value={serialNumber}
                      onChange={(e) => setSerialNumber(e.target.value)}
                      required
                    />
                  </div>

                  <div className="MachineFormField">
                    <label className="MachineFormLabel">
                      <Calendar />
                      Purchase Date
                    </label>
                    <input
                      type="date"
                      className="MachineFormInput"
                      value={purchaseDate}
                      onChange={(e) => setPurchaseDate(e.target.value)}
                      required
                    />
                  </div>

                  <div className="MachineFormField">
                    <label className="MachineFormLabel">
                      <MapPin />
                      Current Location
                    </label>
                    <input
                      type="text"
                      className="MachineFormInput"
                      value={currentLocation}
                      onChange={(e) => setCurrentLocation(e.target.value)}
                      required
                    />
                  </div>

                  <div className="MachineFormField">
                    <label className="MachineFormLabel">
                      <User />
                      Operator Name
                    </label>
                    <input
                      type="text"
                      className="MachineFormInput"
                      value={operatorName}
                      onChange={(e) => setOperatorName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="MachineFormField">
                    <label className="MachineFormLabel">
                      <Fuel />
                      Fuel Capacity
                    </label>
                    <input
                      type="text"
                      className="MachineFormInput"
                      value={fuleCapacity}
                      onChange={(e) => setFuleCapacity(e.target.value)}
                      required
                    />
                  </div>

                  {/* Usage Type - Radio Buttons */}
                  <div className="MachineFormField">
                    <label className="MachineFormLabel">
                      <Fuel />
                      Usage Type
                    </label>
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "8px" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="usageType"
                          value="PER_KM"
                          checked={usageType === "PER_KM"}
                          onChange={() => setUsageType("PER_KM")}
                        />
                        Per Kilometer (km per liter)
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="usageType"
                          value="PER_HOUR"
                          checked={usageType === "PER_HOUR"}
                          onChange={() => setUsageType("PER_HOUR")}
                        />
                        Per Hour (liters per hour)
                      </label>
                    </div>
                  </div>

                  <div className="MachineFormField">
                    <label className="MachineFormLabel">
                      <Fuel />
                      {usageType === "PER_KM"
                        ? "Fuel Efficiency (km per liter)"
                        : "Fuel Consumption (liters per hour)"}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="MachineFormInput"
                      value={fuelEfficiency}
                      onChange={(e) => setFuelEfficiency(e.target.value)}
                      placeholder={usageType === "PER_KM" ? "e.g. 4.5" : "e.g. 12.8"}
                      required
                    />
                  </div>

                  {/* <div className="MachineFormField">
                    <label className="MachineFormLabel">
                      <Fuel />
                      Current Fuel (liters)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="MachineFormInput"
                      value={currentFuel}
                      onChange={(e) => setCurrentFuel(e.target.value)}
                      placeholder="0"
                    />
                  </div> */}

                  <div className="MachineFormField MachineFormFieldFull">
                    <label className="MachineFormLabel">
                      <FileText />
                      Description
                    </label>
                    <textarea
                      className="MachineFormTextarea"
                      rows="4"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="MachineFormActions">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddMachineForm(false);
                      resetMachineForm();
                    }}
                    className="MachineFormCancel"
                  >
                    Cancel
                  </button>
                  <button type="submit" disabled={loading} className="MachineFormSubmit">
                    <Save />
                    {editId ? "Update Machine" : "Save Machine"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Machine;